import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Copy, Check, Gift, ArrowRight, Sparkles, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { supabase } from '../config/supabase';
import { toast } from 'sonner';

export default function ReferralSection() {
  const { user } = useAuth();
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [inputCode, setInputCode] = useState('');
  const [hasReferrer, setHasReferrer] = useState(false);
  const [rewardsCount, setRewardsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user) {
      fetchReferralData();
    }
  }, [user]);

  const fetchReferralData = async () => {
    try {
      // Fetch user's referral code and referrer status
      // We need to query by uid because the auth user object uses uid
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('referral_code, referred_by, id')
        .eq('uid', user?.uid)
        .single();

      if (userError) throw userError;

      setReferralCode(userData.referral_code);
      setHasReferrer(!!userData.referred_by);

      // Fetch rewards count
      if (userData.id) {
        const { count, error: countError } = await supabase
          .from('referral_rewards')
          .select('*', { count: 'exact', head: true })
          .eq('referrer_id', userData.id);

        if (!countError) {
          setRewardsCount(count || 0);
        }
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    if (referralCode) {
      const link = `${window.location.origin}/ref/${referralCode}`;
      navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Link de convite copiado!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleApplyCode = async () => {
    if (!inputCode.trim()) return;
    setApplying(true);
    try {
      const { data, error } = await supabase.rpc('apply_referral_code', {
        p_code: inputCode.trim()
      });

      if (error) throw error;

      if (data.success) {
        toast.success(data.message);
        setHasReferrer(true);
        setInputCode('');
        // Refresh data to ensure UI is consistent
        fetchReferralData();
      } else {
        toast.error(data.message);
      }
    } catch (error: any) {
      console.error('Error applying code:', error);
      toast.error(error.message || 'Erro ao aplicar código');
    } finally {
      setApplying(false);
    }
  };

  if (loading) return null;

  return (
    <section className="mb-32 relative overflow-hidden rounded-3xl border border-cyan-500/30 bg-gradient-to-br from-cyan-950/30 via-black to-black p-1">
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100 contrast-150 mix-blend-overlay"></div>
      
      <div className="relative bg-black/40 backdrop-blur-sm rounded-[22px] p-8 md:p-12 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-cyan-500/10 blur-[120px] rounded-full pointer-events-none -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-purple-500/10 blur-[100px] rounded-full pointer-events-none translate-y-1/2 -translate-x-1/4" />

        <div className="relative z-10 flex flex-col lg:flex-row gap-12 items-start lg:items-center">
          
          {/* Left Column: Info & Stats */}
          <div className="flex-1 space-y-8 w-full">
            <div>
              <div className="inline-flex items-center px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-xs font-bold uppercase tracking-wider mb-4">
                <Gift className="w-3 h-3 mr-2" />
                Programa de Indicação
              </div>
              <h3 className="text-3xl md:text-5xl font-black text-white mb-4">
                Convide e Ganhe <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
                  200 Tokens
                </span>
              </h3>
              <p className="text-gray-400 text-lg max-w-xl">
                Para cada amigo que você convidar e se tornar ativo (fizer uma recarga ou assinatura), você ganha 200 tokens automaticamente.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-cyan-500/20 rounded-lg text-cyan-400">
                    <Users size={20} />
                  </div>
                  <span className="text-sm font-bold text-gray-400 uppercase">Amigos Indicados</span>
                </div>
                <div className="text-3xl font-black text-white">{rewardsCount}</div>
              </div>
              
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-purple-500/20 rounded-lg text-purple-400">
                    <Sparkles size={20} />
                  </div>
                  <span className="text-sm font-bold text-gray-400 uppercase">Tokens Ganhos</span>
                </div>
                <div className="text-3xl font-black text-white">{rewardsCount * 200}</div>
              </div>
            </div>
          </div>

          {/* Right Column: Actions */}
          <div className="flex-1 w-full bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
            
            {/* Your Code */}
            <div className="mb-8">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                Seu link de convite
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-black/50 border border-white/10 rounded-xl px-4 py-4 font-mono text-sm md:text-base text-cyan-400 font-bold tracking-wider text-center select-all truncate">
                  {referralCode ? `${typeof window !== 'undefined' ? window.location.origin : ''}/ref/${referralCode}` : 'Carregando...'}
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleCopyCode}
                  className="p-4 bg-cyan-500 hover:bg-cyan-400 text-black rounded-xl transition-colors shrink-0"
                  title="Copiar link"
                >
                  {copied ? <Check size={24} /> : <Copy size={24} />}
                </motion.button>
              </div>
              <p className="mt-3 text-xs text-gray-500 flex items-center gap-1.5">
                <Sparkles size={12} className="text-yellow-500" />
                Compartilhe o link. Quando eles entrarem e ativarem a conta, você ganha!
              </p>
            </div>

            {/* Enter Code (Only if not referred yet) */}
            {!hasReferrer ? (
              <div className="pt-8 border-t border-white/10">
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                  Tem um código de convite?
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={inputCode}
                    onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                    placeholder="Digite o código aqui"
                    className="flex-1 bg-white/5 border border-white/10 focus:border-cyan-500/50 rounded-xl px-4 py-3 text-white placeholder-gray-600 outline-none transition-all uppercase font-mono"
                  />
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleApplyCode}
                    disabled={applying || !inputCode}
                    className="px-6 py-3 bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    {applying ? 'Aplicando...' : (
                      <>
                        Aplicar <ArrowRight size={18} />
                      </>
                    )}
                  </motion.button>
                </div>
              </div>
            ) : (
              <div className="pt-6 border-t border-white/10">
                 <div className="flex items-center gap-3 text-green-400 bg-green-500/10 border border-green-500/20 px-4 py-3 rounded-xl">
                    <ShieldCheck size={20} />
                    <span className="font-bold text-sm">Você já foi indicado por um amigo!</span>
                 </div>
              </div>
            )}

          </div>

        </div>
      </div>
    </section>
  );
}
